import { createApi } from "@reduxjs/toolkit/query/react";
import axios from "axios";

import { setAccountStatus, setProfile } from "../../features/auth/auth.slice";
import {
  AccountStatusResponseType,
  IUser,
  IUserProfile,
  LoginInput,
  RegisterInput
} from "../../features/auth/auth.types";
import axiosBaseQuery, { getToken } from "../root";

const baseUrl = `${
  import.meta.env.VITE_API_DOMAIN ? import.meta.env.VITE_API_DOMAIN : "/api"
}`;

const baseQuery = axiosBaseQuery({
  baseUrl
});

export const AuthRTKService = createApi({
  reducerPath: "AuthRTKService",
  baseQuery,
  tagTypes: [
    "EmailActive",
    "PlanActive",
    "Profile",
    "Preferences",
    "CheckStatus"
  ],
  endpoints: (builder) => ({
    registerApi: builder.mutation<any, RegisterInput>({
      query: (registerData) => ({
        url: `/auth/register`,
        method: "POST",
        data: {
          ...registerData,
          user_confirm_password: registerData.user_password
        }
      }),
      transformResponse: (response) => response.element
    }),
    login: builder.mutation<any, LoginInput>({
      query: (loginData) => ({
        url: `/auth/login`,
        method: "POST",
        data: loginData
      }),
      transformResponse: (response) => response.element
    }),
    deleteUser: builder.mutation<void, void>({
      query: () => ({
        url: `/auth/user`,
        method: "DELETE",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element
    }),
    socialAuthLogin: builder.mutation<any, any>({
      query: (loginData) => ({
        url: `/auth/social/login`,
        method: "POST",
        data: loginData
      }),
      transformResponse: (response) => response.element
    }),
    accountStatus: builder.mutation<AccountStatusResponseType, undefined>({
      query: () => ({
        url: `/profile/status`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(setAccountStatus(data));
        } catch (error) {}
      }
    }),
    resendVerificationEmail: builder.mutation<any, string>({
      query: (user_identification) => ({
        url: `/profile/validation-link/${user_identification}`,
        method: "POST"
      })
    }),
    forgotPassword: builder.mutation<any, string>({
      query: (user_email) => ({
        url: "/auth/password",
        method: "PUT",
        data: {
          email_address: user_email
        }
      })
    }),
    resetPassword: builder.mutation<
      any,
      { user_password: string; token: string; user_identification: string }
    >({
      query: ({ user_identification, token, user_password }) => ({
        url: `/auth/password/${user_identification}`,
        method: "PATCH",
        data: {
          new_password: user_password,
          new_confirm_password: user_password,
          token
        }
      })
    }),
    getProfile: builder.query<IUser, void>({
      query: () => ({
        url: `/profile`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      providesTags: ["Profile"],
      transformResponse: (response) => response.element,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setProfile(data));
        } catch (error) {}
      }
    }),
    updateProfile: builder.mutation<any, IUserProfile>({
      query: (data) => ({
        url: "/profile",
        method: "PATCH",
        headers: {
          "x-auth-token": getToken()
        },
        data
      }),
      invalidatesTags: ["Profile"]
    }),
    updateAvatar: builder.mutation<any, any>({
      query: (data) => ({
        url: "/profile/avatar",
        method: "PATCH",
        headers: {
          "x-auth-token": getToken()
        },
        data
      })
    }),
    updatePassword: builder.mutation<any, any>({
      query: (data) => ({
        url: "/profile/password",
        method: "POST",
        headers: {
          "x-auth-token": getToken()
        },
        data
      })
    }),
    updateEmail: builder.mutation<any, any>({
      query: (data) => ({
        url: "/profile/email",
        method: "PATCH",
        headers: {
          "x-auth-token": getToken()
        },
        data
      })
    }),
    checkEmailStatus: builder.query<any, void>({
      query: () => ({
        url: "/profile/status",
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      providesTags: ["CheckStatus"]
    }),
    checkEmail: builder.query<any, string>({
      query: (email) => ({
        url: `/profile/email/${email}`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      })
    }),
    checkUsername: builder.query<any, string>({
      query: (username) => ({
        url: `/profile/username/${username}`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      })
    }),
    contactUs: builder.mutation<
      any,
      { email?: string; name?: string; message?: string }
    >({
      query: (data) => ({
        url: "/ui/contact-us",
        method: "POST",
        headers: {
          "x-auth-token": getToken()
        },
        data
      })
    }),
    deleteProfileAvatar: builder.mutation<any, void>({
      query: () => ({
        url: "/profile/avatar",
        method: "DELETE",
        headers: {
          "x-auth-token": getToken()
        }
      })
    }),
    freeSubscription: builder.mutation<any, undefined>({
      query: () => ({
        url: "/payment/v1/eec/create-subscription",
        method: "POST",
        headers: {
          "x-auth-token": getToken()
        }
      })
    }),
    updateProfileData: builder.mutation<any, any>({
      query: (data) => ({
        url: "/profile/update/references",
        method: "PATCH",
        headers: {
          "x-auth-token": getToken()
        },
        data
      }),
      invalidatesTags: ["Profile"]
    }),
    getPreferences: builder.query<any, undefined>({
      query: () => ({
        url: "/profile/preferences",
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      providesTags: ["Preferences"],
      transformResponse: (response) => response.element
    }),
    updatePreferences: builder.mutation<any, any>({
      query: (data) => ({
        url: "/profile/preferences",
        method: "PATCH",
        headers: {
          "x-auth-token": getToken()
        },
        data: {
          user_data: data
        }
      })
      // invalidatesTags: ['Preferences']
    }),
    setError: builder.mutation<any, { error: string; message: string }>({
      query: (data) => ({
        url: "/auth/Error",
        method: "POST",
        headers: {
          "x-auth-token": getToken()
        },
        data: {
          ...data,
          applicationName: "LouiseBotFrontend"
        }
      })
    }),
    passLessLogin: builder.mutation<
      any,
      { user_email: string; secret_word: string }
    >({
      query: (data) => ({
        url: "/auth/passless/login",
        method: "POST",
        data
      })
      // transformResponse: (response) => response.element
    }),
    passLessStatus: builder.mutation<
      any,
      {
        user_identification: string;
        secret_word: string;
      }
    >({
      query: ({
        user_identification,
        secret_word
      }: {
        user_identification: string;
        secret_word: string;
      }) => ({
        url: `/auth/passless/login/status/${user_identification}`,
        method: "POST",
        data: {
          secret_word: secret_word
        }
      })
      // transformResponse: (response) => response.element
    }),
    socialAuthCheckUser: builder.mutation<
      any,
      {
        type: string;
        key?: string;
        partnerInfo?: {
          firstName: string;
          lastName: string;
          birthDate: string;
          companyName: string;
          address1: string;
          address2: string;
          city: string;
          country: string;
          zipCode: string;
          phone: string;
          websiteLinks: string;
          promotionPlan: string;
          monthlyViews: string;
          ambitions: string;
          termsAndConditions: true;
          textReaded: true;
          payoutInfo: {};
        };
        adminPanel?: true;
        providerPayload?: {
          access_token: string;
          authuser: string;
          expires_in: 0;
          hd: string;
          prompt: string;
          provider: string;
          scope: string;
          token_type: string;
        };
        user_utm_data?: {};
      }
    >({
      query: (data) => ({
        url: `/auth/social/check-user`,
        method: "POST",
        data: data
      }),
      transformResponse: (response) => response.element
    }),
    passLessLoginFinalize: builder.mutation<
      any,
      { user_identification: string; passless_token: string }
    >({
      query: ({ user_identification, passless_token }) => ({
        url: `/auth/passless/login/finalize/${user_identification}`,
        method: "POST",
        data: {
          passless_token
        }
      }),
      transformResponse: (response) => response.element
    }),
    refreshToken: builder.mutation<any, { refresh_token: string }>({
      query: ({ refresh_token }) => ({
        url: `/auth/refresh`,
        method: "POST",
        data: {
          refresh_token: refresh_token
        }
      })
      // transformResponse: (response) => response.element
    })
  })
});

export const getAppPasswordLevel = async () => {
  try {
    const { data: response } = await axios.get(
      `https://sum-c.galloni-si.com/umd/api/entity/NB8ZESMNNNEK8OM2HBQ1/preferences`,
      {
        headers: {
          authorization:
            "Basic QU1GSFNPMVZaRDhIWUhURk1NV0U6bGtuQmI2M2xsbUhIT3JDbk5aVVpX"
        }
      }
    );

    return response;
  } catch (error) {
    throw error;
  }
};

export const {
  useRegisterApiMutation,
  useLoginMutation,
  useSocialAuthLoginMutation,
  useAccountStatusMutation,
  useResendVerificationEmailMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useUpdateAvatarMutation,
  useUpdatePasswordMutation,
  useUpdateEmailMutation,
  useContactUsMutation,
  useDeleteProfileAvatarMutation,
  useFreeSubscriptionMutation,
  useUpdateProfileDataMutation,
  useGetPreferencesQuery,
  useUpdatePreferencesMutation,
  useSetErrorMutation,
  useLazyCheckEmailQuery,
  useLazyCheckEmailStatusQuery,
  useLazyGetProfileQuery,
  useDeleteUserMutation,
  usePassLessLoginMutation,
  usePassLessStatusMutation,
  usePassLessLoginFinalizeMutation,
  useSocialAuthCheckUserMutation,
  useRefreshTokenMutation
} = AuthRTKService;
